import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Stack,
  Button,
} from "@mui/material";
import {
  CalendarToday,
  CheckCircle,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";
import { useDashboard } from "../../../contexts/DashboardContext";

export interface LotData {
  _key: string;
  _id: string;
  _rev: string;
  farm_id: string;
  name: string;
  description?: string;
  cattle_tags: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LotListData {
  data: LotData[];
  resumo?: string;
  url?: string;
}

export interface LotListViewProps {
  lotListData: LotListData;
}

const formatDate = (dateString: string | undefined, t: any): string => {
  if (!dateString) return t('common.date_not_available');
  try {
    // Se a data está no formato YYYY-MM-DD, evita problemas de timezone
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    // Para outros formatos, usa a conversão normal
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};



export const LotListView: React.FC<LotListViewProps> = ({ lotListData }) => {
  const { t } = useTranslation(['farm', 'common']);
  const { data: lots } = lotListData;
  const { updateContent } = useDashboard();
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const totalPages = Math.ceil(lots.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = lots.slice(startIndex, startIndex + itemsPerPage);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: DESIGN_TOKENS.colors.text.primary,
            mb: 1,
          }}
        >
          {t('titles.lot_list', { ns: 'farm' })}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
        >
          {lotListData.resumo || t('labels.lots_found', { ns: 'farm', count: lots.length })}
        </Typography>
      </Box>

      {/* Summary Card */}
      <Card sx={{ mb: 3, bgcolor: DESIGN_TOKENS.colors.background.elevated }}>
        <CardContent>
          <Stack direction="row" spacing={4} alignItems="center">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img 
              src="/gestores/lote-branco.svg" 
              alt={t('labels.lots', { ns: 'farm' })} 
              style={{ width: 32, height: 32 }}
            />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('labels.total_lots', { ns: 'farm' })}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {lots.length}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircle sx={{ color: DESIGN_TOKENS.colors.status.success }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('labels.active_lots', { ns: 'farm' })}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {lots.filter(lot => lot.is_active).length}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img 
                 src="/gestores/lote-branco.svg" 
                 alt={t('labels.animals', { ns: 'farm' })} 
                 style={{ width: 32, height: 32 }}
               />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('labels.total_animals', { ns: 'farm' })}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {lots.reduce((total, lot) => total + lot.cattle_tags.length, 0)}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Lots Grid */}
      {currentItems.length > 0 ? (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {currentItems.map((lot) => (
              <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)', lg: 'calc(33.333% - 16px)' } }} key={lot._key}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 3,
                    },
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    const detailPayload = {
                      data: {
                        _key: lot._key,
                        _id: lot._id,
                        _rev: lot._rev,
                        farm_id: lot.farm_id,
                        name: lot.name,
                        description: lot.description || null,
                        cattle_tags: (Array.isArray(lot.cattle_tags) ? lot.cattle_tags : []).map((tag) => ({ name: tag, tag, identifier: tag })),
                        is_active: lot.is_active,
                        created_at: lot.created_at,
                        updated_at: lot.updated_at,
                      },
                      resumo: t('labels.details_description', { ns: 'farm' }),
                      url: '/Lotes/detalhes',
                    };
                    updateContent('lotDetails', detailPayload);
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Header do Lote */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: DESIGN_TOKENS.colors.organization.farm,
                          width: 56,
                          height: 56,
                        }}
                      >
                        <img 
                          src="/gestores/lote-branco.svg" 
                          alt={t('labels.cattle_lot', { ns: 'farm' })} 
                          style={{ width: 32, height: 32 }}
                        />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: DESIGN_TOKENS.colors.text.primary,
                          }}
                        >
                          {lot.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                        >
                          ID: {lot._key}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Descrição */}
                    {lot.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: DESIGN_TOKENS.colors.text.secondary,
                          mb: 2,
                          fontStyle: "italic",
                        }}
                      >
                        {lot.description}
                      </Typography>
                    )}

                    {/* Informações do Lote */}
                    <Stack spacing={1.5}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <img 
                           src="/gestores/lote-branco.svg" 
                           alt={t('labels.animals', { ns: 'farm' })} 
                           style={{ width: 20, height: 20, opacity: 0.6 }}
                         />
                        <Typography variant="body2">
                          <strong>{lot.cattle_tags.length}</strong> {t('labels.animals', { ns: 'farm' })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarToday
                          sx={{
                            fontSize: 16,
                            color: DESIGN_TOKENS.colors.text.secondary,
                          }}
                        />
                        <Typography variant="body2">
                          {t('labels.created_at', { ns: 'farm' })} {formatDate(lot.created_at, t)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarToday
                          sx={{
                            fontSize: 16,
                            color: DESIGN_TOKENS.colors.text.secondary,
                          }}
                        />
                        <Typography variant="body2">
                          {t('labels.updated_at', { ns: 'farm' })} {formatDate(lot.updated_at, t)}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Tags dos Animais */}
                    {lot.cattle_tags.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              color: DESIGN_TOKENS.colors.text.primary,
                            }}
                          >
                            {t('labels.animals_in_lot', { ns: 'farm' })}:
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {lot.cattle_tags.slice(0, 3).map((tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.75rem",
                                }}
                              />
                            ))}
                            {lot.cattle_tags.length > 3 && (
                              <Chip
                                label={`+${lot.cattle_tags.length - 3}`}
                                size="small"
                                sx={{
                                  bgcolor: DESIGN_TOKENS.colors.neutral[100],
                                  fontSize: "0.75rem",
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 4,
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<NavigateBefore />}
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                {t('buttons.previous', { ns: 'common' })}
              </Button>
              <Typography variant="body2" sx={{ mx: 2 }}>
                {t('labels.page_of', { ns: 'common', current: currentPage, total: totalPages })}
              </Typography>
              <Button
                variant="outlined"
                endIcon={<NavigateNext />}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                {t('buttons.next', { ns: 'common' })}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <img 
               src="/gestores/lote-branco.svg" 
               alt={t('empty_states.no_lots', { ns: 'farm' })} 
               style={{ 
                 width: 80, 
                 height: 80, 
                 opacity: 0.3,
                 marginBottom: 16
               }}
             />
            <Typography
              variant="h6"
              sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}
            >
              {t('empty_states.no_lots', { ns: 'farm' })}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
            >
              {t('empty_states.no_lots_description', { ns: 'farm' })}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default LotListView;
